import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  CircularProgress,
  Typography,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/constants';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get session_id from URL query params
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    // Fetch subscription details
    const fetchSubscriptionStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/stripe/subscription-status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        
        if (data.success) {
          setSubscription(data.subscription);
        } else {
          setError(data.message || 'Failed to fetch subscription details');
        }
      } catch (err) {
        console.error('Error fetching subscription details:', err);
        setError('Failed to fetch subscription details');
      } finally {
        setLoading(false);
      }
    };

    // Wait a moment to ensure Stripe webhook has processed
    const timer = setTimeout(() => {
      fetchSubscriptionStatus();
    }, 2000);

    return () => clearTimeout(timer);
  }, [location.search]);

  const handleContinue = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '80vh' 
        }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Processing your payment...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            textAlign: 'center'
          }}>
            <Typography variant="h5" color="error" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" paragraph>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleContinue}
              sx={{ mt: 2 }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center'
        }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Payment Successful!
          </Typography>
          <Typography variant="body1" paragraph>
            Thank you for subscribing to LeaveWise. Your payment has been processed successfully.
          </Typography>
          
          {subscription && (
            <Box sx={{ mb: 3, width: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Subscription Details
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                borderBottom: '1px solid #eee', 
                py: 1 
              }}>
                <Typography variant="body1">Plan:</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {subscription.plan}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                borderBottom: '1px solid #eee', 
                py: 1 
              }}>
                <Typography variant="body1">Status:</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {subscription.status}
                </Typography>
              </Box>
              {subscription.currentPeriodEnd && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  borderBottom: '1px solid #eee', 
                  py: 1 
                }}>
                  <Typography variant="body1">Next billing date:</Typography>
                  <Typography variant="body1">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleContinue}
            size="large"
            sx={{ mt: 2 }}
          >
            Continue to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentSuccess;
